import React from 'react';
import { TypographyProps } from './Typography.types';
import { typographyConfig, fontWeightMap } from './Typography.config';
import styles from './Typography.module.css';

const Typography: React.FC<TypographyProps> = ({
  variant,
  weight = 'regular',
  as = 'p',
  color,
  children,
  className = '',
  style = {},
  ariaLabel,
  testId,
}) => {
  const Element = as;
  const config = typographyConfig[variant];

  // Validation: Check if weight is available for this variant
  if (
    process.env.NODE_ENV === 'development' &&
    !config.availableWeights.includes(weight)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Typography] Weight "${weight}" is not available for variant "${variant}". ` +
        `Available weights: ${config.availableWeights.join(', ')}. Falling back to "regular".`
    );
  }

  // Determine the correct font family based on variant and weight
  const baseFontFamily = config.fontFamily;
  const weightConfig = fontWeightMap[weight];
  const fontFamilyWithWeight = weightConfig.fontFamily
    ? baseFontFamily.replace('Regular', weightConfig.fontFamily)
    : baseFontFamily;

  const computedStyle: React.CSSProperties = {
    fontFamily: `${fontFamilyWithWeight}, sans-serif`,
    fontSize: config.fontSize,
    lineHeight: config.lineHeight,
    letterSpacing: config.letterSpacing,
    fontWeight: weightConfig.fontWeight,
    color: color,
    ...style,
  };

  const classNames = [
    styles.typography,
    styles[`typography--${variant}`],
    styles[`typography--${weight}`],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element
      className={classNames}
      style={computedStyle}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {children}
    </Element>
  );
};

Typography.displayName = 'Typography';

export default Typography;
export { Typography };
export type {
  TypographyProps,
  TypographyVariant,
  TypographyWeight,
} from './Typography.types';
